import { Color, Spacing } from '@persona-id/design-tokens';
import { sortBy, uniq } from 'lodash/fp';
import { DateTime } from 'luxon';
import { ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom-v5-compat';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled, { css } from 'styled-components';
import { v4 as uuid } from 'uuid';

import ContextMenu from 'dashboard/components/ContextMenu';
import ModalWrapper from 'dashboard/components/ModalWrapper';
import Permissioned from 'dashboard/components/Permissioned';
import Tooltip from 'dashboard/components/Tooltip';
import ConfirmationModal from 'dashboard/components-lib/ConfirmationModal';
import MentionExtension from 'dashboard/components-lib/RichTextEditor/Mentions/MentionsExtension';
import RichTextEditor from 'dashboard/components-lib/RichTextEditor/RichTextEditor';
import FormAddCaseComment, { AddCaseCommentFormData } from 'dashboard/forms/FormAddCaseComment';
import { userDisplayName } from 'dashboard/lib/valueDisplayers/userDisplayName';
import { CaseComment } from 'dashboard/models/case-comment';
import { CaseCommentTemplate } from 'dashboard/models/case-template';
import { User, UserResponseAttributes } from 'dashboard/models/user';
import { Workflow } from 'dashboard/models/workflow';
import { WorkflowRun, WorkflowRunType } from 'dashboard/models/workflow-run';
import { WorkflowRunStepAction } from 'dashboard/models/workflow-step';
import { ModuleCaseComments } from 'dashboard/modules/CaseComments';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import Spacer from 'design-system/components/Spacer';
import { Paragraph } from 'design-system/components/Text';
import IconUserProfilePicture from 'images/icons/user-profile-picture.svg';
import {
  DATE_AND_TIME_FORMAT,
  LONG_FORM_DATE,
  MONTH_AND_DAY,
  SHORT_TIME_FORMAT,
} from 'lib/constants/luxonFormats';
import useConfirmation from 'lib/hooks/useConfirmation';
import { ApiModel } from 'lib/interfaces/api';
import { Permission } from 'lib/permissions';
import { captureMessage } from 'lib/reporting';

const CaseCommentsCard = styled(Card)<{ dynamicHeight: boolean }>`
  margin-bottom: 0;
  margin-top: 0;
  ${({ dynamicHeight }) =>
    dynamicHeight
      ? css`
          height: auto;
        `
      : css`
          height: 100%;
          display: grid;
          grid-template-rows: minmax(0, 1fr) auto auto;
        `}
`;

const CaseCommentsCardBody = styled(Card.Body)<{ dynamicHeight: boolean }>`
  grid-gap: 0;
  ${({ dynamicHeight }) =>
    dynamicHeight
      ? css`
          height: auto;
          grid-template-rows: auto auto auto;
        `
      : css`
          height: 100%;
          grid-template-rows: auto auto 1fr;
        `}
`;

const CommentsContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${Spacing[4]};
  align-content: start;
  overflow-y: auto;
`;

const CommentContainer = styled('div')`
  display: grid;
  grid-template-columns: 1fr;
  grid-row-gap: ${Spacing[2]};
`;

const SubtitleContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: auto auto 1fr;
  align-items: center;
`;

const CommentContent = styled('div')`
  white-space: pre-wrap;
`;

const ContextMenuContainer = styled('div')`
  justify-self: end;
  margin-right: 10px;
  margin-top: 4px;
`;

const ButtonContainer = styled('div')`
  display: grid;
  grid-column-gap: ${Spacing[2]};
  grid-template-columns: auto auto;
  max-width: 150px;
  margin-bottom: 10px;
  margin-left: 1px;
`;

const TooltipContent = styled('div')`
  font-size: small;
`;

const StyledDropdownItem = styled(ContextMenu.Item)`
  width: 100%;
  height: 35px;
  font-size: small;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const HorizontalLine = styled.hr`
  border: none;
  border-top: 1px solid ${Color.BorderHairline};
`;

const PROFILE_PICTURE_WIDTH = 42;
const PROFILE_PICTURE_HEIGHT = 42;

const ProfilePictureImage = styled('img')`
  border-radius: ${BORDER_RADIUSES.circle};
  object-fit: cover;
`;

const StyledIconUserProfilePicture = styled(IconUserProfilePicture)`
  border-radius: ${BORDER_RADIUSES.circle};
`;

const NameAndDateContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
`;
const MENTION_DATA_ID = 'data-id';

export const parseMentionData = (html: string) => {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const mentionElements = doc.querySelectorAll('*[data-type="mention"]');

  const mentionData: string[] = [];
  mentionElements.forEach((span) => {
    const id = span.getAttribute(MENTION_DATA_ID);
    if (id) {
      mentionData.push(id);
    }
  });

  return uniq(mentionData);
};

interface IndividualCaseCommentProps {
  comment: CaseComment;
  users: User[];
  workflows: Workflow[];
  workflowActions: WorkflowRunStepAction[];
  workflowRuns: WorkflowRun[];
  editCaseComment: (message: string, id: string, mentionedUsers: string[]) => Promise<void>;
  removeCaseComment: (id: string) => Promise<void>;
  currentUser: User;
}

const IndividualCaseComment = ({
  comment,
  users,
  workflows,
  workflowActions,
  workflowRuns,
  editCaseComment,
  removeCaseComment,
  currentUser,
}: IndividualCaseCommentProps) => {
  const [message, setMessage] = useState(comment.attributes.message);
  const [deleteCaseComment, { prompt, confirm, cancel }] = useConfirmation();

  const [editMode, setEditMode] = useState(false);
  const handleSave = async () => {
    if (comment.attributes.message !== message) {
      const previouslyMentionedUsers = parseMentionData(comment.attributes.message);
      const mentionedUsers = parseMentionData(message);

      const newMentionedUsers = mentionedUsers.filter(
        (mentionedUser) => !previouslyMentionedUsers.includes(mentionedUser),
      );

      await editCaseComment(message, comment.id, newMentionedUsers);
    }
    setEditMode(false);
  };
  const commenterData = comment.relationships.commenter.data;

  let commenterName: ReactNode;
  let profilePictureUrl: ReactNode;
  switch (commenterData?.type) {
    case ApiModel.User: {
      const commenter = users.find((user) => user.id === commenterData.id);
      if (commenter) {
        commenterName = userDisplayName(commenter) || '';
        profilePictureUrl = (commenter.attributes as UserResponseAttributes)['profile-picture-url'];
      }
      break;
    }
    case ApiModel.WorkflowRunStepAction: {
      // TODO: (melissa) remove this when we've backfilled everything to workflow run ids
      const workflowAction = workflowActions.find((action) => action.id === commenterData.id);
      const workflowRun = workflowRuns.find(
        (run) => run.id === workflowAction?.relationships['workflow-run'].data.id,
      );
      const workflow = workflows.find(
        (wfl) => wfl.id === workflowRun?.relationships.workflow.data.id,
      );
      if (workflow) {
        if (!workflowRun) {
          captureMessage(
            'Encountered an unexpected error, workflow-run could not be found when parsing case commenter',
            'error',
            { commentId: comment.id },
          );
          commenterName = '';
          break;
        }

        const isCaseAction = workflowRun.attributes['run-type'] === WorkflowRunType.Case;
        const workflowRunLink = `/dashboard/${isCaseAction ? 'case-actions' : 'workflows'}/${
          workflow.id
        }/runs/${workflowRun.id}`;

        commenterName = isCaseAction ? (
          <Link to={workflowRunLink}>Case Action: {workflow.attributes.name}</Link>
        ) : (
          <Link to={workflowRunLink}>Workflow: {workflow.attributes.name}</Link>
        );
      } else {
        commenterName = 'Workflow';
      }

      break;
    }
    case ApiModel.WorkflowRun: {
      const workflowRun = workflowRuns.find((run) => run.id === commenterData.id);
      const workflow = workflows.find(
        (wfl) => wfl.id === workflowRun?.relationships.workflow.data.id,
      );
      if (workflow) {
        if (!workflowRun) {
          captureMessage(
            'Encountered an unexpected error, workflow-run could not be found when parsing case commenter',
            'error',
            { commentId: comment.id },
          );
          commenterName = '';
          break;
        }

        const isCaseAction = workflowRun.attributes['run-type'] === WorkflowRunType.Case;
        const workflowRunLink = `/dashboard/${isCaseAction ? 'case-actions' : 'workflows'}/${
          workflow.id
        }/runs/${workflowRun.id}`;

        commenterName = isCaseAction ? (
          <Link to={workflowRunLink}>Case Action: {workflow.attributes.name}</Link>
        ) : (
          <Link to={workflowRunLink}>Workflow: {workflow.attributes.name}</Link>
        );
      } else {
        commenterName = 'Workflow';
      }

      break;
    }
    default: {
      commenterName = '';
    }
  }

  const createdAt = comment.attributes['created-at'];
  const editedAt = comment.attributes['edited-at'];
  const createdAtTooltip = createdAt
    ? DateTime.fromISO(createdAt).toFormat(DATE_AND_TIME_FORMAT)
    : null;

  const currentTime = DateTime.local();
  const currentUtcTime = currentTime.toUTC();
  const twentyFourHoursAgo = currentUtcTime.minus({ hours: 24 });

  const editedAtDisplay = editedAt
    ? DateTime.fromISO(editedAt).toUTC() > twentyFourHoursAgo
      ? DateTime.fromISO(editedAt).toRelative()
      : DateTime.fromISO(editedAt).toFormat(DATE_AND_TIME_FORMAT)
    : null;

  const utcCreatedAt = DateTime.fromISO(createdAt).toUTC();
  const isSameDay = utcCreatedAt.startOf('day').toISO() === currentTime.startOf('day').toISO();
  const isSameMonth =
    utcCreatedAt.startOf('month').toISO() === currentTime.startOf('month').toISO();
  const isSameYear = utcCreatedAt.startOf('year').toISO() === currentTime.startOf('year').toISO();
  const isSameDate = isSameDay && isSameMonth && isSameYear;

  const createdAtDisplay = isSameDate
    ? DateTime.fromISO(createdAt).toFormat(SHORT_TIME_FORMAT)
    : isSameYear
      ? DateTime.fromISO(createdAt).toFormat(MONTH_AND_DAY)
      : DateTime.fromISO(createdAt).toFormat(LONG_FORM_DATE);

  const currentUserPermission = commenterData?.id === currentUser.id;

  const isEdited = comment.attributes['edited-at'] != null;

  const handleRemoveComment = () => prompt(() => removeCaseComment(comment.id));

  return (
    <CommentContainer key={comment.id} data-test={`case-comments__comment--${comment.id}`}>
      <ModalWrapper isOpen={deleteCaseComment} handleCloseModal={cancel}>
        <ConfirmationModal
          cancelAction={cancel}
          confirmAction={confirm}
          header='Delete comment?'
          confirmActionText='Delete'
        >
          Are you sure you want to delete this comment?
        </ConfirmationModal>
      </ModalWrapper>
      <SubtitleContainer>
        {profilePictureUrl ? (
          <ProfilePictureImage
            height={PROFILE_PICTURE_HEIGHT}
            width={PROFILE_PICTURE_WIDTH}
            src={profilePictureUrl as string}
          />
        ) : (
          <StyledIconUserProfilePicture
            height={PROFILE_PICTURE_HEIGHT}
            width={PROFILE_PICTURE_WIDTH}
          />
        )}
        <NameAndDateContainer>
          <Row>
            {currentUserPermission ? (
              <Paragraph color='primary'>{commenterName} (you)</Paragraph>
            ) : (
              <Paragraph color='primary'>{commenterName}</Paragraph>
            )}
          </Row>
          <Row>
            <Tooltip
              trigger='hover'
              tooltip={<TooltipContent>{createdAtTooltip}</TooltipContent>}
              hideArrow={true}
              placement='top-start'
            >
              <Paragraph color='secondary'>{createdAtDisplay}</Paragraph>
            </Tooltip>
          </Row>
        </NameAndDateContainer>
        <ContextMenuContainer>
          <Permissioned permission={Permission.CaseWrite}>
            {currentUserPermission && !editMode && (
              <ContextMenu
                variant='inverse'
                items={[
                  {
                    id: 'edit',
                    label: 'Edit',
                    onClick: () => {
                      setEditMode(true);
                    },
                  },
                  {
                    id: 'delete',
                    label: 'Delete',
                    variant: 'danger',
                    onClick: () => {
                      handleRemoveComment();
                    },
                  },
                ]}
                renderItem={(item) => (
                  <StyledDropdownItem
                    id={item.id}
                    onClick={() => {
                      item.onClick?.();
                    }}
                    key={item.id}
                    variant={item.variant}
                  >
                    {item.label}
                  </StyledDropdownItem>
                )}
              />
            )}
          </Permissioned>
        </ContextMenuContainer>
      </SubtitleContainer>
      <CommentContent>
        {currentUserPermission && editMode ? (
          <div>
            <RichTextEditor
              // adding keys to get editor to re-render
              key='readOnlyFalse'
              defaultValue={comment.attributes.message}
              readOnly={false}
              onChange={setMessage}
              additionalEditorExtensions={[MentionExtension({ users })]}
            />
            <Spacer height={2} />
            <ButtonContainer>
              <Button
                id='individual-case-comment__cancel'
                variant='basic'
                width='auto'
                onClick={() => {
                  setEditMode(false);
                }}
              >
                Cancel
              </Button>
              <Button
                id='individual-case-comment__save'
                variant='basic'
                disabled={message === ''}
                width='auto'
                onClick={handleSave}
              >
                Save
              </Button>
            </ButtonContainer>
          </div>
        ) : (
          <div>
            <RichTextEditor
              key='readOnlyTrue'
              defaultValue={comment.attributes.message}
              readOnly={true}
              additionalEditorExtensions={[MentionExtension({ users })]}
            />
            {isEdited && (
              <Tooltip
                trigger='hover'
                tooltip={<TooltipContent>{editedAtDisplay}</TooltipContent>}
                hideArrow={true}
                placement='top-start'
              >
                <Paragraph color='secondary'>(edited)</Paragraph>
              </Tooltip>
            )}
          </div>
        )}
      </CommentContent>
    </CommentContainer>
  );
};

export interface CaseCommentsProps {
  module: ModuleCaseComments;
  caseComments: CaseComment[];
  caseCommentTemplates: CaseCommentTemplate[];
  users: User[];
  workflows: Workflow[];
  workflowActions: WorkflowRunStepAction[];
  workflowRuns: WorkflowRun[];
  isPreview?: boolean;
  isPrint?: boolean;
  scope?: string; // scope differentiates comment drafts from one module/case to another
  addComment: (values: AddCaseCommentFormData, mentionedUsers: string[]) => Promise<void>;
  editCaseComment: (message: string, id: string, mentionedUsers: string[]) => Promise<void>;
  removeCaseComment: (id: string) => Promise<void>;
}

const CaseComments = ({
  module,
  caseComments,
  caseCommentTemplates,
  users,
  workflows,
  workflowActions,
  workflowRuns,
  isPreview = false,
  isPrint = false,
  scope = uuid(), // default to a new scope for each module
  addComment,
  editCaseComment,
  removeCaseComment,
}: CaseCommentsProps) => {
  const commentListRef = useRef<HTMLDivElement>(null);
  const hasDynamicHeight = module['module-layout']['dynamic-height'];
  useLayoutEffect(() => {
    // We want to scroll the latest comment into view if it overflows
    commentListRef.current?.scrollTo({
      top: commentListRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [caseComments]);

  const currentUser = useSelector((state: RootState) => state.currentUser.currentUser);

  const handleAddCaseComment = (data: AddCaseCommentFormData) => {
    const mentionedUsers = parseMentionData(data.message);
    addComment(data, mentionedUsers);
  };

  const sortedCaseComments = sortBy(
    (caseComment) => caseComment.attributes['created-at'],
    caseComments,
  );
  const activeCaseComments = sortedCaseComments.filter((caseComment) => {
    return caseComment.attributes['archived-at'] == null;
  });

  const comments = activeCaseComments.map((comment) => {
    return (
      <IndividualCaseComment
        key={comment.id}
        comment={comment}
        users={users}
        workflows={workflows}
        workflowActions={workflowActions}
        workflowRuns={workflowRuns}
        editCaseComment={editCaseComment}
        removeCaseComment={removeCaseComment}
        currentUser={currentUser}
      />
    );
  });

  return (
    <CaseCommentsCard dynamicHeight={hasDynamicHeight} data-test='module__case-comments'>
      <CaseCommentsCardBody dynamicHeight={hasDynamicHeight}>
        <Card.Title>Comments</Card.Title>
        {comments.length > 0 ? (
          <>
            <Spacer height={1} />
            <CommentsContainer ref={commentListRef}>{comments}</CommentsContainer>
          </>
        ) : (
          <Permissioned
            permission={Permission.CaseWrite}
            fallback={
              <>
                <Spacer height={1} />
                <Paragraph>No comments added for this case.</Paragraph>
              </>
            }
          >
            {null}
          </Permissioned>
        )}
      </CaseCommentsCardBody>
      {!isPrint && (
        <Permissioned permission={Permission.CaseWrite}>
          <HorizontalLine />
          <CaseCommentsCardBody dynamicHeight={hasDynamicHeight}>
            <FormAddCaseComment
              scope={scope}
              addComment={handleAddCaseComment}
              disableForm={isPreview}
              caseCommentTemplates={caseCommentTemplates}
              users={users}
            />
          </CaseCommentsCardBody>
        </Permissioned>
      )}
    </CaseCommentsCard>
  );
};

export default CaseComments;
